import React, { useEffect, useState } from "react";
import { H2, Button, P } from "@upsolve/ui";
import { ModalClose, ViewportCenteredFixed } from "../modals/Modal";
import { StyledOverlayBackdrop } from "../modals/StyledOverlayBackdrop";
import { TriageModal, TriageModalBody } from "../modals/TriageModal";
import { postUserInterestLog, track } from "../analytics/track";
import { EVENTS } from "@upsolve/shared";
import { $TSFixMe } from "@upsolve/shared/dist/types";

type DebtType = "creditCardPersonalLoan" | "mortgage" | "carLoan" | "medicalDebt" | "taxDebt";
type TProduct = "stopCollection" | "debtDispute" | "settleDebt" | "talkToLawyer" | "bankruptcy";

type OnHideFunction = (...args: any[]) => void;

interface ProductRenderData {
  header?: string;
  buttonUrl?: string;
  buttonText?: string;
  message?: string;
  bodyElement?: (onClickFindALawyerLink: () => void) => React.ReactNode;
}

interface ProductSelectorModalProps {
  onHide: OnHideFunction;
  trackingPageContext: $TSFixMe;
}

const findALawyerBodyElement = (onClickFindALawyerLink: () => void) => (
  <>
    <p>
      In the meantime, you can{" "}
      <a
        href={"https://upsolve.org/find-a-lawyer/"}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClickFindALawyerLink}
      >
        request a free debt evaluation
      </a>{" "}
      from a lawyer and read our step-by-step guide to dealing with your debt.
    </p>
  </>
);

const selectedServiceToComingSoonCopyMainServiceIndex: Partial<
  Record<TProduct, ProductRenderData | Partial<Record<DebtType, ProductRenderData>>>
> = {
  // MainService without indexing DebtType goes here
  talkToLawyer: {
    header: "We can help!",
    message:
      "Click here to request a free debt evaluation from a trusted lawyer. First we'll check to see if you're eligible to use our software tools to eliminate your debt.",
    // We are not using the default button and instead using the body element to track the button click
    bodyElement: (onClickFindALawyerLink) => (
      <>
        <a href="https://upsolve.org/find-a-lawyer/" target="_blank">
          <Button onClick={onClickFindALawyerLink} style={{ marginRight: "10px" }}>
            Request free evaluation
          </Button>
        </a>
      </>
    ),
  },
};

const selectedServiceToComingSoonCopyDebtTypeIndex: Partial<
  Record<DebtType, ProductRenderData | Partial<Record<TProduct, ProductRenderData>>>
> = {
  // If we do not have a result from selectedServiceToComingSoonCopyMainServiceIndex then...
  // DebtType indexing MainService goes here
  creditCardPersonalLoan: {
    debtDispute: {
      header: "You have options!",
      buttonUrl: "https://upsolve.org/learn/dispute-debt/",
      buttonText: "Learn More",
      message: "We’re working on app to help you dispute your credit card or personal loan debt.",
      bodyElement: findALawyerBodyElement,
    },
    stopCollection: {
      header: "You have options!",
      buttonUrl: "https://upsolve.org/learn/stop-collection-calls",
      buttonText: "Learn More",
      message: "We’re working on app to help you stop credit card debt or personal loan collection calls.",
      bodyElement: findALawyerBodyElement,
    },
    settleDebt: {
      header: "You have options!",
      buttonUrl: "https://upsolve.org/learn/negotiating-with-debt-collectors/",
      buttonText: "Learn More",
      message: "We’re working on app to help you settle your credit card or personal loan debt.",
      bodyElement: findALawyerBodyElement,
    },
  },

  // If the above isn't found then...
  // DebtType without indexing MainService goes here
  carLoan: {
    header: "You have options!",
    buttonUrl: "https://upsolve.org/learn/cant-afford-your-car-payment/",
    buttonText: "Learn More",
    message: "We’re working on app to help you get relief from your car loan.",
    bodyElement: findALawyerBodyElement,
  },
  taxDebt: {
    header: "You have options!",
    buttonUrl: "https://upsolve.org/learn/offer-in-compromise/",
    buttonText: "Learn More",
    message:
      "You may be able to reduce or eliminate your Tax Debt by negotiating with the IRS through a process called “Offer in Compromise.” We're working on an app to help you do this.",
    bodyElement: findALawyerBodyElement,
  },
  medicalDebt: {
    header: "You have options!",
    buttonUrl: "https://upsolve.org/learn/getting-out-of-medical-debt/",
    buttonText: "Learn More",
    message: "We’re working on app to help you get relief from your medical debt.",
    bodyElement: findALawyerBodyElement,
  },
  mortgage: {
    header: "You have options!",
    buttonUrl: "https://upsolve.org/learn/missed-mortgage-payments/",
    buttonText: "Learn More",
    message: "We’re working on app to help you get relief from your mortgage.",
    bodyElement: findALawyerBodyElement,
  },
};

const mainServiceToDebtTypeQuestion: Partial<Record<TProduct, string>> = {
  stopCollection: "What type of debt are the collection calls about?",
  debtDispute: "What type of debt do you want to dispute?",
  settleDebt: "What type of debt do you want to negotiate?",
  talkToLawyer: "What type of debt do you want to talk with a lawyer about?",
};

export const ProductSelectorModal: React.FC<ProductSelectorModalProps> = ({ onHide, trackingPageContext }) => {
  const [product, setProduct] = useState<TProduct | undefined>();
  const [debtType, setDebtType] = useState<DebtType | undefined>();

  useEffect(() => {
    track(EVENTS.COMPONENT_ACTION, {
      componentName: "debtTriage",
      componentVersion: "0",
      actionId: `debtTriage.opened`,
      actionMethod: "click",
      pageContext: trackingPageContext,
    });
  }, []);

  const handleClose = async () => {
    const step = !product ? "productSelect" : !debtType ? "debtTypeSelect" : "comingSoon";
    track(EVENTS.COMPONENT_ACTION, {
      componentName: "debtTriageClosed",
      componentVersion: "0",
      actionId: `debtTriage.closed.${step}`,
      actionMethod: "click",
      pageContext: trackingPageContext,
    });

    trackUserInterest();
    onHide();
  };

  const handleSelectDebtType = (selectedDebtType: DebtType) => {
    track(EVENTS.COMPONENT_ACTION, {
      componentName: "debtTriageDebtTypeSelected",
      componentVersion: "0",
      actionId: `debtTriage.debtTypeSelected.${selectedDebtType}`,
      actionMethod: "click",
      pageContext: trackingPageContext,
    });

    setDebtType(selectedDebtType);
  };

  const onClickExploreBankruptcyButton = async () => {
    await track(EVENTS.COMPONENT_ACTION, {
      componentName: "debtTriageBankruptcyButton",
      componentVersion: "0",
      actionId: `debtTriage.myUpsolveNavigation`,
      actionMethod: "click",
      pageContext: trackingPageContext,
    });
    setProduct("bankruptcy");
    trackUserInterest(true);
    window.open("https://my.upsolve.org/intro", "_blank");
    onHide();
  };

  const onClickLearnMoreButton = async (url: string) => {
    await track(EVENTS.COMPONENT_ACTION, {
      componentName: "debtTriageLearnMoreButton",
      componentVersion: "0",
      actionId: `debtTriage.learnMore.${debtType}.${product}`,
      actionMethod: "click",
      pageContext: trackingPageContext,
    });

    trackUserInterest();
    window.open(url, "_blank");
    onHide();
  };

  const onClickFindALawyerLink = async () => {
    track(EVENTS.COMPONENT_ACTION, {
      componentName: "debtTriageFindLawyer",
      componentVersion: "0",
      actionId: `debtTriage.findLawyer.${debtType}.${product}`,
      actionMethod: "click",
      pageContext: trackingPageContext,
    });
    trackUserInterest();
    onHide();
  };

  const trackUserInterest = async (trackBankruptcy: boolean = false) =>
    await postUserInterestLog({ productInterest: trackBankruptcy ? "bankruptcy" : product, debtType: debtType });

  const CurrentStep = () => {
    if (!product)
      return (
        <div style={{ textAlign: "center" }}>
          <H2 style={{ fontFamily: "Castoro" }}>What can we help you do today?</H2>
          <div style={{ marginTop: "20px" }}>
            <Button onClick={onClickExploreBankruptcyButton} style={{ marginBottom: "10px" }}>
              Explore bankruptcy
            </Button>
            <Button inverted onClick={() => handleProductSelection("stopCollection")} style={{ marginBottom: "10px" }}>
              Stop debt collection calls
            </Button>
            <Button inverted onClick={() => handleProductSelection("settleDebt")} style={{ marginBottom: "10px" }}>
              Negotiate with a debt collector
            </Button>
            <Button inverted onClick={() => handleProductSelection("debtDispute")} style={{ marginBottom: "10px" }}>
              Dispute a debt
            </Button>
            <Button inverted onClick={() => handleProductSelection("talkToLawyer")} style={{ marginBottom: "10px" }}>
              Talk to a lawyer about a debt
            </Button>
          </div>
        </div>
      );
    else if (!debtType) {
      const debtTypeQuestion = mainServiceToDebtTypeQuestion[product];

      return (
        <div style={{ textAlign: "center" }}>
          {debtTypeQuestion && <H2 style={{ fontFamily: "Castoro" }}>{debtTypeQuestion}</H2>}
          <P>This tool can help you with one debt at a time.</P>
          <div style={{ marginTop: "20px" }}>
            <Button onClick={() => handleSelectDebtType("creditCardPersonalLoan")} style={{ marginBottom: "10px" }}>
              Credit Card or Personal Loan
            </Button>
            <Button inverted onClick={() => handleSelectDebtType("mortgage")} style={{ marginBottom: "10px" }}>
              Mortgage
            </Button>
            <Button inverted onClick={() => handleSelectDebtType("carLoan")} style={{ marginBottom: "10px" }}>
              Car Loan
            </Button>
            <Button inverted onClick={() => handleSelectDebtType("medicalDebt")} style={{ marginBottom: "10px" }}>
              Medical Debt
            </Button>
            <Button inverted onClick={() => handleSelectDebtType("taxDebt")} style={{ marginBottom: "10px" }}>
              Tax Debt
            </Button>
          </div>
        </div>
      );
    }

    const serviceInfo = ((
      selectedServiceToComingSoonCopyMainServiceIndex[product] as Partial<Record<DebtType, ProductRenderData>>
    )?.[debtType] ||
      selectedServiceToComingSoonCopyMainServiceIndex[product] ||
      (selectedServiceToComingSoonCopyDebtTypeIndex[debtType] as Partial<Record<TProduct, ProductRenderData>>)?.[
        product
      ] ||
      selectedServiceToComingSoonCopyDebtTypeIndex[debtType] ||
      {}) as ProductRenderData;

    return (
      <div>
        <H2 style={{ fontFamily: "Castoro" }}>{serviceInfo.header}</H2>
        <P>{serviceInfo.message}</P>

        {serviceInfo.bodyElement && <div>{serviceInfo.bodyElement(onClickFindALawyerLink)}</div>}

        {serviceInfo.buttonUrl && (
          <Button onClick={() => onClickLearnMoreButton(serviceInfo.buttonUrl!)}>{serviceInfo.buttonText}</Button>
        )}
      </div>
    );
  };

  const handleProductSelection = (selectedProduct: TProduct) => {
    setProduct(selectedProduct);
    track(EVENTS.COMPONENT_ACTION, {
      componentName: "debtTriageProductSelected",
      componentVersion: "0",
      actionId: `debtTriage.productSelected.${selectedProduct}`,
      actionMethod: "click",
      pageContext: trackingPageContext,
    });
  };

  return (
    <>
      <ViewportCenteredFixed>
        <TriageModal>
          <ModalClose onClick={handleClose} />
          <TriageModalBody>
            <CurrentStep />
          </TriageModalBody>
        </TriageModal>
      </ViewportCenteredFixed>
      <StyledOverlayBackdrop onClick={handleClose} />
    </>
  );
};
